import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { formatCurrency } from "@/lib/formatters";
import { ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function SpendPacingChart({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["dashboard-kpi-summary", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("kpi-summary", params));
      if (!res.ok) throw new Error("Failed to fetch data");
      return res.json();
    },
  });

  const totalSpend = data?.current?.spend ?? 0;
  const empty = !isLoading && !error && totalSpend === 0;
  const startD = new Date(startDate);
  const endD = new Date(endDate);
  const totalDays = Math.max(1, Math.round((endD.getTime() - startD.getTime()) / (1000 * 60 * 60 * 24)));
  const elapsed = Math.max(1, Math.round((new Date().getTime() - startD.getTime()) / (1000 * 60 * 60 * 24)));
  const dailyRate = totalSpend / elapsed;
  const projectedSpend = dailyRate * totalDays;
  const monthlyBudget = dailyRate * 30;
  const pacingPct = Math.min(100, Math.round((elapsed / totalDays) * 100));
  const spendPct = monthlyBudget > 0 ? Math.min(100, Math.round((totalSpend / monthlyBudget) * 100)) : 0;

  const diff = spendPct - pacingPct;
  const status = diff > 10 ? "Overpacing" : diff < -10 ? "Underpacing" : "On Pace";
  const statusColor = status === "On Pace" ? "text-emerald-600 bg-emerald-50" : status === "Overpacing" ? "text-amber-600 bg-amber-50" : "text-red-600 bg-red-50";

  const chartData = [
    { name: "Spent", value: spendPct },
    { name: "Remaining", value: 100 - spendPct },
  ];

  return (
    <DashboardWidget title="Spend Pacing" widgetType="chart" isEditMode={isEditMode} isLoading={isLoading} error={error?.message ?? null} onRetry={() => refetch()} onRemove={onRemove} onResize={onResize} onResetSize={onResetSize}>
      {empty ? (
        <div className="flex items-center justify-center h-full text-sm" style={{ color: "#5F6D82" }} data-testid="empty-spend-pacing">No spend data for this period</div>
      ) : (
        <div className="px-4 pb-3 h-full flex flex-col items-center justify-center" data-testid="chart-spend-pacing">
          <div className="w-32 h-32 relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={chartData} innerRadius={40} outerRadius={55} dataKey="value" startAngle={90} endAngle={-270} strokeWidth={0}>
                  <Cell fill="#6366F1" />
                  <Cell fill="#DCE3ED" />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg font-bold text-gray-900">{spendPct}%</span>
            </div>
          </div>
          <div className="mt-2 text-center">
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColor}`}>{status}</span>
            <p className="text-xs text-gray-500 mt-1.5">Est. month-end: {formatCurrency(projectedSpend)}</p>
          </div>
        </div>
      )}
    </DashboardWidget>
  );
}
